import { useNavigate } from "@tanstack/react-router"
import { Button } from "@aix/ui"
import { trpc } from "~/lib/trpc"

type CoachProgressStatus = "on-track" | "behind" | "ahead" | "no-plan"

interface CoachProgress {
  coachId: string
  name: string
  icon: string
  completionRate: number
  completedActions: number
  totalActions: number
  status: CoachProgressStatus
}

const statusStyles: Record<CoachProgressStatus, { bg: string; border: string; text: string; label: string }> = {
  "on-track": {
    bg: "bg-primary/5",
    border: "border-primary",
    text: "text-primary",
    label: "On Track",
  },
  ahead: {
    bg: "bg-green-50",
    border: "border-green-500",
    text: "text-green-700",
    label: "Ahead",
  },
  behind: {
    bg: "bg-destructive/5",
    border: "border-destructive",
    text: "text-destructive",
    label: "Behind",
  },
  "no-plan": {
    bg: "bg-muted",
    border: "border-border",
    text: "text-muted-foreground",
    label: "No Plan",
  },
}

function CoachProgressCard({ coach }: { coach: CoachProgress }) {
  const navigate = useNavigate()
  const styles = statusStyles[coach.status]

  const handleClick = () => {
    navigate({
      to: "/coach-os/coach/$coachId",
      params: { coachId: coach.coachId },
    })
  }

  const handleCatchUp = (e: React.MouseEvent) => {
    e.stopPropagation()
    navigate({
      to: "/coach-os/coach/$coachId",
      params: { coachId: coach.coachId },
      // @ts-ignore
      search: { tab: "plans" },
    })
  }

  return (
    <div
      className={`border-2 ${styles.border} ${styles.bg} rounded-lg p-4 transition-all hover:shadow-md cursor-pointer`}
      onClick={handleClick}
    >
      <div className="space-y-3">
        {/* Coach name and icon */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <span className="text-2xl">{coach.icon}</span>
            <h3 className="font-bold text-foreground text-sm">{coach.name}</h3>
          </div>
          <span className={`text-xs font-medium ${styles.text}`}>{styles.label}</span>
        </div>

        {/* Stats */}
        {coach.status !== "no-plan" ? (
          <>
            <div className="space-y-1">
              <div className="flex items-center justify-between text-xs">
                <span className="text-muted-foreground">
                  {coach.completedActions}/{coach.totalActions} actions
                </span>
                <span className={`font-bold ${styles.text}`}>{coach.completionRate}%</span>
              </div>

              {/* Progress bar */}
              <div className="w-full bg-border rounded-full h-2 overflow-hidden">
                <div
                  className={`h-full transition-all duration-300 ${
                    coach.status === "ahead"
                      ? "bg-green-500"
                      : coach.status === "on-track"
                        ? "bg-primary"
                        : "bg-destructive"
                  }`}
                  style={{ width: `${coach.completionRate}%` }}
                />
              </div>
            </div>

            {/* Action button */}
            <div>
              {coach.status === "behind" ? (
                <Button onClick={handleCatchUp} size="sm" variant="destructive" className="w-full text-xs">
                  Catch Up →
                </Button>
              ) : (
                <Button onClick={handleClick} size="sm" variant="outline" className="w-full text-xs">
                  Details →
                </Button>
              )}
            </div>
          </>
        ) : (
          <div className="space-y-2">
            <p className="text-xs text-muted-foreground">No weekly plan set</p>
            <Button onClick={handleCatchUp} size="sm" variant="outline" className="w-full text-xs">
              Start Week →
            </Button>
          </div>
        )}
      </div>
    </div>
  )
}

export default function ThisWeeksProgressGrid() {
  const { data, isLoading } = trpc.coachDashboard.getWeeklyProgressSummary.useQuery()
  const navigate = useNavigate()

  if (isLoading) {
    return (
      <div className="space-y-4">
        <h2 className="text-lg font-bold text-foreground">🗓️ This Week&apos;s Progress</h2>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
          {[1, 2, 3, 4].map((i) => (
            <div key={i} className="border-2 border-border rounded-lg p-4 animate-pulse">
              <div className="h-6 bg-muted rounded mb-3"></div>
              <div className="h-4 bg-muted rounded mb-2"></div>
              <div className="h-2 bg-muted rounded mb-3"></div>
              <div className="h-8 bg-muted rounded"></div>
            </div>
          ))}
        </div>
      </div>
    )
  }

  if (!data || data.coaches.length === 0) {
    return (
      <div className="space-y-4">
        <h2 className="text-lg font-bold text-foreground">🗓️ This Week&apos;s Progress</h2>
        <div className="border-2 border-border rounded-lg p-8 text-center bg-muted/30">
          <p className="text-sm text-muted-foreground mb-4">
            No active coaches yet. Create your first coach to start tracking progress!
          </p>
          <Button
            onClick={() =>
              navigate({
                to: "/coach-os",
                // @ts-ignore
                search: (prev) => ({ ...prev, tab: "templates" }),
              })
            }
          >
            Browse Templates →
          </Button>
        </div>
      </div>
    )
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h2 className="text-lg font-bold text-foreground">🗓️ This Week&apos;s Progress</h2>
        <Button
          onClick={() =>
            navigate({
              to: "/coach-os",
              // @ts-ignore
              search: (prev) => ({ ...prev, tab: "list" }),
            })
          }
          variant="ghost"
          size="sm"
          className="text-xs"
        >
          View All Coaches →
        </Button>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
        {data.coaches.map((coach) => (
          <CoachProgressCard key={coach.coachId} coach={coach} />
        ))}
      </div>
    </div>
  )
}
